<template>
  <transition name="picker-fade">
    <div
      v-if="visible"
      class="mention-picker"
      :style="placement === 'above'
        ? { left: x + 'px', bottom: y + 'px' }
        : { left: x + 'px', top: y + 'px' }"
      @keydown.down.prevent="highlightNext"
      @keydown.up.prevent="highlightPrev"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.esc="$emit('close')"
    >
      <!-- Header: muestra la query activa -->
      <div class="picker-header">
        <span class="picker-at">@</span>
        <span class="picker-query" v-if="query">{{ query }}</span>
        <span class="picker-query placeholder" v-else>buscar usuario…</span>
      </div>

      <!-- Filtros de tipo, solo visibles cuando hay resultados mixtos -->
      <div class="picker-tabs" v-if="hasMultipleTypes && !loading">
        <button
          v-for="tab in visibleTabs"
          :key="tab.key"
          type="button"
          class="picker-tab"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <span class="tab-icon">{{ tab.icon }}</span>
          {{ tab.label }}
          <span class="tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <!-- Lista de resultados -->
      <div class="picker-body">
        <div v-if="loading" class="picker-loading">
          <div class="loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>

        <div v-else-if="noResults" class="picker-empty">
          Sin resultados para <strong>{{ query }}</strong>
        </div>

        <div v-else class="picker-list" ref="listEl" :style="{ maxHeight: listMaxHeight + 'px' }">
          <!-- Usuarios -->
          <template v-if="showGroup('users') && filteredUsers.length">
            <div class="group-label" v-if="!activeTab || activeTab === 'all'">Usuarios</div>
            <div
              v-for="(u, i) in filteredUsers"
              :key="'u-' + u.id"
              class="picker-item"
              :class="{ highlighted: isHighlighted(flatIndex('users', i)) }"
              @click="selectItem(normalizeUser(u))"
              @mouseenter="highlighted = flatIndex('users', i)"
            >
              <div class="item-avatar user-avatar">
                <img v-if="u.avatar" :src="u.avatar" :alt="u.name" />
                <span v-else>{{ initials(u.name) }}</span>
              </div>
              <div class="item-info">
                <span class="item-title" v-html="highlight(u.name)"></span>
                <span class="item-sub" v-if="u.email">{{ u.email }}</span>
              </div>
            </div>
          </template>

          <!-- Tareas -->
          <template v-if="showGroup('tasks') && filteredTasks.length">
            <div class="group-label" v-if="!activeTab || activeTab === 'all'">Tareas</div>
            <div
              v-for="(t, i) in filteredTasks"
              :key="'t-' + t.id"
              class="picker-item"
              :class="{ highlighted: isHighlighted(flatIndex('tasks', i)) }"
              @click="selectItem(normalizeTask(t))"
              @mouseenter="highlighted = flatIndex('tasks', i)"
            >
              <div class="item-avatar task-avatar">
                <v-icon small>mdi-clipboard-text-outline</v-icon>
              </div>
              <div class="item-info">
                <span class="item-title" v-html="highlight(t.title)"></span>
                <span class="item-sub" v-if="t.number">#{{ t.number }}</span>
              </div>
            </div>
          </template>

          <!-- RDIs -->
          <template v-if="showGroup('rdis') && filteredRdis.length">
            <div class="group-label" v-if="!activeTab || activeTab === 'all'">RDI</div>
            <div
              v-for="(r, i) in filteredRdis"
              :key="'r-' + r.id"
              class="picker-item"
              :class="{ highlighted: isHighlighted(flatIndex('rdis', i)) }"
              @click="selectItem(normalizeRdi(r))"
              @mouseenter="highlighted = flatIndex('rdis', i)"
            >
              <div class="item-avatar rdi-avatar">
                <v-icon small>mdi-file-document-multiple-outline</v-icon>
              </div>
              <div class="item-info">
                <span class="item-title" v-html="highlight(r.title)"></span>
                <span class="item-sub" v-if="r.number">#{{ r.number }}</span>
              </div>
            </div>
          </template>

          <!-- Adicionales -->
          <template v-if="showGroup('additionals') && filteredAdditionals.length">
            <div class="group-label" v-if="!activeTab || activeTab === 'all'">Adicionales</div>
            <div
              v-for="(a, i) in filteredAdditionals"
              :key="'a-' + a.id"
              class="picker-item"
              :class="{ highlighted: isHighlighted(flatIndex('additionals', i)) }"
              @click="selectItem(normalizeAdditional(a))"
              @mouseenter="highlighted = flatIndex('additionals', i)"
            >
              <div class="item-avatar additional-avatar">
                <v-icon small>mdi-notebook-plus-outline</v-icon>
              </div>
              <div class="item-info">
                <span class="item-title" v-html="highlight(a.title || a.number)"></span>
              </div>
            </div>
          </template>

          <!-- Entregas -->
          <template v-if="showGroup('entregas') && filteredEntregas.length">
            <div class="group-label" v-if="!activeTab || activeTab === 'all'">Entregas</div>
            <div
              v-for="(e, i) in filteredEntregas"
              :key="'e-' + e.id"
              class="picker-item"
              :class="{ highlighted: isHighlighted(flatIndex('entregas', i)) }"
              @click="selectItem(normalizeEntrega(e))"
              @mouseenter="highlighted = flatIndex('entregas', i)"
            >
              <div class="item-avatar entrega-avatar">
                <v-icon small>mdi-format-list-checks</v-icon>
              </div>
              <div class="item-info">
                <span class="item-title" v-html="highlight(e.title)"></span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Footer hint -->
      <div class="picker-footer">
        <span>Selecciona el elemento al quieres mencionar</span>
      </div>
    </div>
  </transition>
</template>

<script>
import { search } from '@/helpers/api/general'

const GROUP_ORDER = ['users', 'tasks', 'rdis', 'additionals', 'entregas']

const TAB_DEFS = {
  users:       { label: 'Usuarios',    icon: '👤', key: 'users' },
  tasks:       { label: 'Tareas',      icon: '✓',  key: 'tasks' },
  rdis:        { label: 'RDI',         icon: 'ⓘ',  key: 'rdis' },
  additionals: { label: 'Adicionales', icon: '+',  key: 'additionals' },
  entregas:    { label: 'Entregas',    icon: '≡',  key: 'entregas' },
}

export default {
  name: 'MentionPicker',

  props: {
    visible:       { type: Boolean, default: false },
    x:             { type: Number, default: 0 },
    y:             { type: Number, default: 0 },
    placement:     { type: String, default: 'below' }, // 'below' | 'above'
    listMaxHeight: { type: Number, default: 280 },
    projectId:     { type: [Number, String], default: null },
    types:         { type: Array, default: () => ['users', 'tasks', 'rdis', 'additionals', 'entregas'] },
    minChars:      { type: Number, default: 1 },
    // La query viene del editor padre (el texto que el usuario escribe tras @)
    query:         { type: String, default: '' },
  },

  emits: ['select', 'close'],

  data() {
    return {
      loading: false,
      results: { tasks: [], rdis: [], additionals: [], entregas: [], users: [] },
      timer: null,
      highlighted: 0,
      activeTab: null, // null = mostrar todos
    }
  },

  computed: {
    // Filtrado local rápido (previo o sin API)
    filteredUsers()       { return this.filterGroup(this.usersList,       ['name', 'email']) },
    filteredTasks()       { return this.filterGroup(this.results.tasks,       ['title', 'number']) },
    filteredRdis()        { return this.filterGroup(this.results.rdis,        ['title', 'number']) },
    filteredAdditionals() { return this.filterGroup(this.results.additionals, ['title', 'number']) },
    filteredEntregas()    { return this.filterGroup(this.results.entregas,    ['title']) },

    totalCount() {
      return (
        this.filteredUsers.length +
        this.filteredTasks.length +
        this.filteredRdis.length +
        this.filteredAdditionals.length +
        this.filteredEntregas.length
      )
    },

		usersList() {
			return this.$store.usersStore.state.usersList
		},
		
    noResults() {
      return !this.loading && this.query.length >= this.minChars && this.totalCount === 0
    },

    hasMultipleTypes() {
      const nonEmpty = GROUP_ORDER.filter(k => this[`filtered${this.capitalize(k)}`]?.length > 0)
      return nonEmpty.length > 1
    },

    visibleTabs() {
      return GROUP_ORDER
        .filter(k => this.types.includes(k) && this[`filtered${this.capitalize(k)}`]?.length > 0)
        .map(k => ({
          ...TAB_DEFS[k],
          count: this[`filtered${this.capitalize(k)}`].length,
        }))
    },
  },

  watch: {
    visible(val) {
      if (!val) {
        this.reset()
      } else {
        // Al abrir, si hay query ya presente, buscar
        this.scheduleSearch()
				if(!this.usersList?.length){
					this.getProjectUserList()
				}
      }
    },
    query() {
      this.highlighted = 0
      this.activeTab = null
      this.scheduleSearch()
    },
  },

  methods: {
		getProjectUserList(){
			const data = {
				project_id: this.$store.projectsStore.state.selectedProject.id,
				start: 0,
				limit: 20
			}
			this.$store.usersStore.dispatch('getUsersList', data)
		},

    scheduleSearch() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => this.performSearch(), 200)
    },

    async performSearch() {
      const q = (this.query || '').trim()
      if (q.length < this.minChars) {
        // Sin query mínima: limpiar resultados no-usuario (solo usuarios se muestran por defecto si el padre cargó una lista inicial)
        this.results = { tasks: [], rdis: [], additionals: [], entregas: [], users: this.results.users }
        return
      }
      this.loading = true
      try {
        const data = { search: q }
        if (this.projectId) data.project_id = this.projectId
        const res = await search(data)
        if (res?.code === 200) {
          this.results.tasks       = res.tasks       || []
          this.results.rdis        = res.rdis        || []
          this.results.additionals = res.additionals || []
          this.results.entregas    = res.entregas    || []
          this.results.users       = res.users       || []
        } else {
          this.results = { tasks: [], rdis: [], additionals: [], entregas: [], users: [] }
        }
      } catch (err) {
        console.error('MentionPicker search error', err)
        this.results = { tasks: [], rdis: [], additionals: [], entregas: [], users: [] }
      } finally {
        this.loading = false
        this.highlighted = 0
      }
    },

    selectItem(item) {
      this.$emit('select', item)
      this.$emit('close')
      this.reset()
    },

    reset() {
      this.results  = { tasks: [], rdis: [], additionals: [], entregas: [], users: [] }
      this.loading  = false
      this.activeTab = null
      if (this.timer) clearTimeout(this.timer)
      this.timer = null
      this.highlighted = 0
    },

    // --- Helpers de display ---

    showGroup(key) {
      if (!this.types.includes(key)) return false
      if (!this.activeTab || this.activeTab === 'all') return true
      return this.activeTab === key
    },

    filterGroup(arr, fields) {
      if (!arr?.length) return []
      const q = (this.query || '').trim().toLowerCase()
      if (!q) return arr
      return arr.filter(item =>
        fields.some(f => String(item[f] || '').toLowerCase().includes(q))
      )
    },

    highlight(text) {
      if (!text) return ''
      const q = (this.query || '').trim()
      if (!q) return this.escapeHtml(text)
      const escaped = this.escapeHtml(text)
      const escapedQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return escaped.replace(
        new RegExp(`(${escapedQ})`, 'gi'),
        '<mark>$1</mark>'
      )
    },

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },

    initials(name) {
      return (name || '')
        .split(' ')
        .slice(0, 2)
        .map(w => w[0]?.toUpperCase() || '')
        .join('')
    },

    capitalize(str) {
      if (str === 'rdis') return 'Rdis'
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    // --- Navegación con teclado ---

    flatIndex(group, index) {
      const visibleGroups = GROUP_ORDER.filter(k => this.showGroup(k))
      let i = 0
      for (const k of visibleGroups) {
        const key = `filtered${this.capitalize(k)}`
        if (k === group) return i + index
        i += this[key]?.length || 0
      }
      return 0
    },

    isHighlighted(idx) { return this.highlighted === idx },

    highlightNext() {
      if (this.totalCount === 0) return
      this.highlighted = (this.highlighted + 1) % this.totalCount
      this.scrollToHighlighted()
    },

    highlightPrev() {
      if (this.totalCount === 0) return
      this.highlighted = (this.highlighted - 1 + this.totalCount) % this.totalCount
      this.scrollToHighlighted()
    },

    scrollToHighlighted() {
      this.$nextTick(() => {
        const el = this.$refs.listEl?.querySelector('.highlighted')
        el?.scrollIntoView({ block: 'nearest' })
      })
    },

    selectHighlighted() {
      let idx = this.highlighted
      const visibleGroups = GROUP_ORDER.filter(k => this.showGroup(k))
      for (const k of visibleGroups) {
        const key = `filtered${this.capitalize(k)}`
        const arr = this[key] || []
        if (idx < arr.length) {
          const normalize = {
            users: this.normalizeUser,
            tasks: this.normalizeTask,
            rdis: this.normalizeRdi,
            additionals: this.normalizeAdditional,
            entregas: this.normalizeEntrega,
          }[k]
          if (normalize) this.selectItem(normalize(arr[idx]))
          return
        }
        idx -= arr.length
      }
    },

    // --- Normalizers ---
    normalizeUser(u)       { return { id: u.id, type: 'user',       label: u.name, meta: u } },
    normalizeTask(t)       { const ref = t.number || t.id; const title = t.title || ''; return { id: t.id, type: 'task',       label: `[Tarea] ${ref}${title ? ' - ' + title : ''}`,      meta: t } },
    normalizeRdi(r)        { const ref = r.number || r.id; const title = r.title || ''; return { id: r.id, type: 'rdi',        label: `[RDI] ${ref}${title ? ' - ' + title : ''}`,        meta: r } },
    normalizeAdditional(a) { const ref = a.number || a.id; const title = a.title || ''; return { id: a.id, type: 'additional', label: `[Adicional] ${ref}${title ? ' - ' + title : ''}`, meta: a } },
    normalizeEntrega(e)    { const ref = e.number || e.id; const title = e.title || ''; return { id: e.id, type: 'entrega',    label: `[Entrega] ${ref}${title ? ' - ' + title : ''}`,   meta: e } },
  },
}
</script>

<style scoped>
/* ─── Variables de marca ─── */
:root {
  --brand-primary: #2D4B95;
  --brand-secondary: #5E85EF;
  --brand-light: #EEF2FC;
  --brand-lighter: #F5F7FD;
}

/* ─── Animación de entrada ─── */
.picker-fade-enter-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.picker-fade-leave-active {
  transition: opacity 0.08s ease, transform 0.08s ease;
}
.picker-fade-enter-from,
.picker-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* ─── Contenedor principal ─── */
.mention-picker {
  position: fixed;
  z-index: 9999;
  width: 296px;
	max-height: 300px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(45, 75, 149, 0.14);
  box-shadow:
    0 0 0 1px rgba(45, 75, 149, 0.06),
    0 4px 6px -1px rgba(45, 75, 149, 0.06),
    0 12px 24px -4px rgba(45, 75, 149, 0.1);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
}

/* ─── Header ─── */
.picker-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px 8px;
  border-bottom: 1px solid rgba(45, 75, 149, 0.07);
  flex: 0 0 auto;
}

.picker-at {
  font-size: 13px;
  font-weight: 600;
  color: var(--brand-primary, #2D4B95);
  line-height: 1;
}

.picker-query {
  font-size: 13px;
  color: var(--brand-primary, #2D4B95);
  font-weight: 500;
}
.picker-query.placeholder {
  color: #aab4cc;
  font-weight: 400;
}

/* ─── Tabs de tipo ─── */
.picker-tabs {
  display: flex;
  gap: 2px;
  padding: 5px 8px;
  border-bottom: 1px solid rgba(45, 75, 149, 0.07);
  overflow-x: auto;
  scrollbar-width: none;
  flex: 0 0 auto;
}
.picker-tabs::-webkit-scrollbar { display: none; }

.picker-tab {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #7a8aaa;
  cursor: pointer;
  border: none;
  background: none;
  white-space: nowrap;
  transition: background 0.1s, color 0.1s;
  line-height: 1.6;
}
.picker-tab:hover {
  background: var(--brand-light, #EEF2FC);
  color: var(--brand-primary, #2D4B95);
}
.picker-tab.active {
  background: var(--brand-light, #EEF2FC);
  color: var(--brand-primary, #2D4B95);
}

.tab-icon {
  font-size: 11px;
  opacity: 0.75;
}

.tab-count {
  font-size: 10px;
  font-weight: 600;
  color: var(--brand-secondary, #5E85EF);
  background: rgba(94, 133, 239, 0.1);
  border-radius: 10px;
  padding: 0 5px;
  line-height: 1.6;
}

/* ─── Cuerpo / lista ─── */
.picker-body {
  padding: 4px 0;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.picker-list {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(45, 75, 149, 0.15) transparent;
  flex: 1 1 auto;
  min-height: 0;
}
.picker-list::-webkit-scrollbar { width: 4px; }
.picker-list::-webkit-scrollbar-thumb {
  background: rgba(45, 75, 149, 0.15);
  border-radius: 4px;
}

/* ─── Etiqueta de grupo ─── */
.group-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #aab4cc;
  padding: 8px 12px 3px;
}

/* ─── Item ─── */
.picker-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.08s;
}
.picker-item:hover {
  background: var(--brand-lighter, #F5F7FD);
}
.picker-item.highlighted {
  background: var(--brand-light, #EEF2FC);
}

/* ─── Avatares / íconos de tipo ─── */
.item-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
}

.user-avatar {
  background: var(--brand-light, #EEF2FC);
  color: var(--brand-primary, #2D4B95);
}
.user-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.task-avatar {
  background: #edf7ee;
  color: #2a7a3b;
  border-radius: 7px;
}
.rdi-avatar {
  background: #fef3e2;
  color: #b45309;
  border-radius: 7px;
}
.additional-avatar {
  background: #f0ecfe;
  color: #6d28d9;
  border-radius: 7px;
}
.entrega-avatar {
  background: #fce7f3;
  color: #9d174d;
  border-radius: 7px;
}

/* ─── Texto del item ─── */
.item-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.item-title {
  font-size: 13px;
  font-weight: 500;
  color: #1a2540;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

/* highlight de búsqueda */
:deep(mark) {
  background: rgba(94, 133, 239, 0.18);
  color: var(--brand-primary, #2D4B95);
  border-radius: 2px;
  padding: 0 1px;
  font-weight: 600;
}

.item-sub {
  font-size: 11px;
  color: #8a96b0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

/* ─── Loading ─── */
.picker-loading {
  display: flex;
  justify-content: center;
  padding: 18px 0;
}
.loading-dots {
  display: flex;
  gap: 5px;
  align-items: center;
}
.loading-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--brand-secondary, #5E85EF);
  animation: dot-bounce 1.2s infinite ease-in-out;
  opacity: 0.5;
}
.loading-dots span:nth-child(1) { animation-delay: 0s; }
.loading-dots span:nth-child(2) { animation-delay: 0.2s; }
.loading-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
  40%            { transform: scale(1);   opacity: 1; }
}

/* ─── Empty ─── */
.picker-empty {
  padding: 18px 12px;
  font-size: 13px;
  color: #9aa0b4;
  text-align: center;
}
.picker-empty strong {
  color: var(--brand-primary, #2D4B95);
  font-weight: 500;
}

/* ─── Footer ─── */
.picker-footer {
  display: flex;
  gap: 10px;
  justify-content: center;
  padding: 5px 12px 7px;
  border-top: 1px solid rgba(45, 75, 149, 0.07);
  font-size: 10px;
  color: #b0bac8;
  letter-spacing: 0.01em;
  flex: 0 0 auto;
}
.picker-footer span {
  display: flex;
  align-items: center;
  gap: 2px;
}
</style>